import { useCallback, useMemo, useState } from "react";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import providers from "lib/providers";

interface Options {
  /** Current message list — used to map selected ids back to messages for
   *  forwarding and to evaluate "canDeleteAny". Must already be in the same
   *  order as the UI renders them. */
  messages: Message[];
  /** Current user id, to decide per-message delete eligibility. */
  authUserId?: string | number;
  /** True when the viewer has moderator rights (group / channel owner) and
   *  therefore may delete non-own messages too. */
  canModerate?: boolean;
  /** Called AFTER a successful bulk delete, with the ids that were deleted
   *  server-side, so the caller can remove them from its local list. */
  onDeleted?: (ids: string[]) => void;
}

export interface MessageSelectionController {
  selectionMode: boolean;
  selectedIds: Set<string>;
  selectedCount: number;
  /** Messages that are both in the current list AND currently selected,
   *  preserving the list's order. Safe to pass to ForwardMessageModal. */
  selectedMessages: Message[];
  /** At least one selected message is deletable by the current user. */
  canDeleteAny: boolean;
  enter: (initial?: Message) => void;
  cancel: () => void;
  toggle: (message: Message) => void;
  /**
   * Delete all selected messages server-side. Runs in parallel; reports any
   * ids that failed. Regardless of partial failure, ids that succeeded are
   * reported to `onDeleted` so the UI doesn't keep stale rows.
   */
  deleteSelected: () => Promise<{ deleted: string[]; failed: string[] }>;
}

/**
 * Shared selection-mode controller for chat pages. Owns the selectedIds set
 * and exposes the primitives the list view and the toolbar need.
 *
 * Keeping this in a hook rather than duplicating useState + handlers in
 * every chat page:
 *  - Guarantees identical bulk-delete semantics across 1-on-1, group and
 *    channel chats.
 *  - Centralises the "what does deletable mean" rule (own or moderator).
 *  - Makes it trivial to add a future ThreadPanel / search-result chat.
 */
export default function useMessageSelection({
  messages,
  authUserId,
  canModerate,
  onDeleted,
}: Options): MessageSelectionController {
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const enter = useCallback((initial?: Message) => {
    setSelectionMode(true);
    setSelectedIds(initial ? new Set([initial.id]) : new Set());
  }, []);

  const cancel = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const toggle = useCallback((message: Message) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(message.id)) next.delete(message.id);
      else next.add(message.id);
      return next;
    });
  }, []);

  // Auto-exit selection mode when the last selected message is removed
  // (either by toggling or by external deletion). Keeps the toolbar from
  // being stuck at "0 selected".
  // Note: we DON'T auto-exit on `enter(undefined)` because the caller
  // explicitly asked for an empty-selection entry point.
  const isDeletable = useCallback(
    (m: Message) => {
      if (!authUserId) return false;
      if (String(m.senderId) === String(authUserId)) return true;
      return !!canModerate;
    },
    [authUserId, canModerate]
  );

  const selectedMessages = useMemo(
    () => messages.filter((m) => selectedIds.has(m.id)),
    [messages, selectedIds]
  );

  const canDeleteAny = useMemo(
    () => selectedMessages.some(isDeletable),
    [selectedMessages, isDeletable]
  );

  const deleteSelected = useCallback(async () => {
    const deletable = selectedMessages.filter(isDeletable);
    if (deletable.length === 0) return { deleted: [], failed: [] };
    // Run in parallel; the server handles per-id auth. Partial success is
    // the common case (e.g. a moderator deleting a mix of own + others,
    // where one id was already deleted by someone else).
    const results = await Promise.allSettled(
      deletable.map((m) => providers.MessagesProvider.delete({ id: m.id }))
    );
    const deleted: string[] = [];
    const failed: string[] = [];
    results.forEach((r, i) => {
      const id = deletable[i].id;
      if (r.status === "fulfilled") deleted.push(id);
      else failed.push(id);
    });
    if (deleted.length > 0 && onDeleted) onDeleted(deleted);
    // Exit selection mode on success (or partial success); the list will
    // re-render without the removed bubbles.
    setSelectionMode(false);
    setSelectedIds(new Set());
    return { deleted, failed };
  }, [selectedMessages, isDeletable, onDeleted]);

  return {
    selectionMode,
    selectedIds,
    // Count live messages only: if a selected message was removed (by
    // socket event or by another user deleting it), selectedIds still
    // contains its id until cleanup, but the toolbar / bulk actions must
    // reflect what's actually actionable.
    selectedCount: selectedMessages.length,
    selectedMessages,
    canDeleteAny,
    enter,
    cancel,
    toggle,
    deleteSelected,
  };
}
